// @ts-ignore
/* eslint-disable */
import { request } from '@umijs/max';
import { getRefreshToken } from '@/utils/token';
import type { API } from './typings';

/** 获取当前的用户 GET /v1/users/me */
export async function currentUser(options?: { [key: string]: any }) {
  return request<API.UserInfoResponse>('/v1/users/me', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 退出登录接口 POST /v1/auth/logout */
export async function outLogin(
  params?: API.LogoutParams,
  options?: { [key: string]: any }
) {
  return request<API.LogoutResult>('/v1/auth/logout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: params || {},
    ...(options || {}),
  });
}

/** 登录接口 POST /v1/auth/login */
export async function login(body: API.LoginParams, options?: { [key: string]: any }) {
  return request<API.LoginResult>('/v1/auth/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: body,
    ...(options || {}),
  });
}

/**
 * 刷新Token接口 POST /v1/auth/refresh
 * 
 * 安全策略：
 * - Token Rotation: 每次刷新时生成新的 refresh token，旧的 refresh token 被撤销
 * - Refresh 次数限制: 超过 max_refresh_count 后需要重新登录
 * - 黑名单检查: 如果提供了 old_access_token，会检查其是否已被撤销
 * - 自动黑名单: 刷新成功后，旧的 access token 会被加入黑名单
 * 
 * 注意：返回的 refresh_token 是新的，旧的 refresh_token 已失效，需要更新本地存储
 * 
 * @param refreshToken 刷新令牌（必需）
 * @param accessToken 当前访问令牌（可选，用于黑名单检查）
 * @param options 其他请求选项
 * @returns 包含新的访问令牌和刷新令牌的响应
 */
export async function refreshToken(
  refreshToken: string,
  accessToken?: string,
  options?: { [key: string]: any }
) {
  // 构建请求参数
  const params: API.RefreshTokenParams = {
    refresh_token: refreshToken,
  };
  
  // 如果提供了access_token，添加到请求中（用于黑名单检查）
  if (accessToken) {
    params.access_token = accessToken;
  }

  return request<API.LoginResult>('/v1/auth/refresh', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    data: params,
    skipErrorHandler: true, // 跳过默认错误处理，自己处理刷新失败
    ...(options || {}),
  });
}

/**
 * 获取用户设备列表 GET /v1/auth/devices
 * 
 * 功能：
 * - 查询用户所有活跃的设备会话
 * - 显示每个设备的详细信息（设备名称、类型、IP、User-Agent）
 * - 标记当前设备（通过 X-Refresh-Token header）
 * - 显示刷新次数（为 Token Rotation 做准备）
 * 
 * @param params 查询参数
 * @param params.require_fresh 是否要求fresh token，默认false
 * @param options 其他请求选项
 * @returns 设备会话列表
 */
export async function getUserDevices(
  params?: {
    require_fresh?: boolean;  // 是否要求fresh token，默认false
  },
  options?: { [key: string]: any }
) {
  // 获取refresh_token用于标记当前设备
  const refreshToken = getRefreshToken();

  return request<API.GetUserDevicesResult>('/v1/auth/devices', {
    method: 'GET',
    params: {
      require_fresh: params?.require_fresh || false,
    },
    headers: {
      // 通过 X-Refresh-Token header 标记当前设备
      ...(refreshToken ? { 'X-Refresh-Token': refreshToken } : {}),
      ...(options?.headers || {}),
    },
    ...(options || {}),
  });
}

/**
 * 批量撤销用户的所有设备会话 DELETE /v1/auth/devices
 * 
 * 功能：
 * - 撤销用户所有设备的会话
 * - 可选择保留当前设备（keep_current=true）
 * 
 * ⚠️ 注意：
 * - 如果 keep_current=false，当前设备也会被撤销
 * - 撤销后这些设备无法再使用 refresh_token 获取新的 access_token
 * - 当前设备的 access_token 仍然有效（30分钟内）
 * 
 * 使用场景：
 * - 怀疑账号被盗，撤销所有其他设备
 * - 更换设备，清理旧设备
 * - 安全审计，清除所有会话
 * 
 * @param params 撤销参数
 * @param params.keep_current 是否保留当前设备，默认false
 *   - 如果为 true 且提供了 X-Refresh-Token header，将保留当前设备
 *   - 如果为 true 但未提供 X-Refresh-Token header，将撤销所有设备（包括当前设备）
 * @param params.require_fresh 是否要求fresh token，默认false
 * @param options 其他请求选项
 * @returns 撤销成功响应
 */
export async function revokeAllDevices(
  params?: API.RevokeAllDevicesParams,
  options?: { [key: string]: any }
) {
  // 获取refresh_token用于保留当前设备
  const refreshToken = getRefreshToken();

  return request<API.RevokeAllDevicesResult>('/v1/auth/devices', {
    method: 'DELETE',
    params: {
      keep_current: params?.keep_current || false,
      require_fresh: params?.require_fresh || false,
    },
    headers: {
      // 通过 X-Refresh-Token header 传递当前设备的 refresh token（用于保留当前设备）
      ...(refreshToken ? { 'X-Refresh-Token': refreshToken } : {}),
      ...(options?.headers || {}),
    },
    ...(options || {}),
  });
}

/**
 * 撤销指定设备的会话 DELETE /v1/auth/devices/{token_hash}
 * 
 * 功能：
 * - 根据 token_hash 撤销指定设备
 * - 验证设备所有权（只能撤销自己的设备）
 * - 撤销后该设备无法再使用 refresh_token 获取新的 access_token
 * 
 * ⚠️ 注意：
 * - 当前设备的 access_token 仍然有效（30分钟内）
 * - 如需立即失效，请使用紧急登出（urgent=true）
 * 
 * @param tokenHash 要撤销的设备的 token_hash（必需）
 * @param params 查询参数
 * @param params.require_fresh 是否要求fresh token，默认false
 * @param options 其他请求选项
 * @returns 撤销成功响应
 * @throws HTTP 401: 设备不存在或无权撤销
 * @throws HTTP 500: 服务器内部错误
 */
export async function revokeDevice(
  tokenHash: string,
  params?: {
    require_fresh?: boolean;  // 是否要求fresh token，默认false
  },
  options?: { [key: string]: any }
) {
  return request<API.RevokeAllDevicesResult>(`/v1/auth/devices/${tokenHash}`, {
    method: 'DELETE',
    params: {
      require_fresh: params?.require_fresh || false,
    },
    ...(options || {}),
  });
}

/** 此处后端没有提供注释 GET /api/notices */
export async function getNotices(options?: { [key: string]: any }) {
  return request<API.NoticeIconList>('/api/notices', {
    method: 'GET',
    ...(options || {}),
  });
}

/** 获取规则列表 GET /api/rule */
export async function rule(
  params: {
    // query
    /** 当前的页码 */
    current?: number;
    /** 页面的容量 */
    pageSize?: number;
  },
  options?: { [key: string]: any },
) {
  return request<API.RuleList>('/api/rule', {
    method: 'GET',
    params: {
      ...params,
    },
    ...(options || {}),
  });
}

/** 更新规则 PUT /api/rule */
export async function updateRule(options?: { [key: string]: any }) {
  return request<API.RuleListItem>('/api/rule', {
    method: 'POST',
    data: {
      method: 'update',
      ...(options || {}),
    },
  });
}

/** 新建规则 POST /api/rule */
export async function addRule(options?: { [key: string]: any }) {
  return request<API.RuleListItem>('/api/rule', {
    method: 'POST',
    data: {
      method: 'post',
      ...(options || {}),
    },
  });
}

/** 删除规则 DELETE /api/rule */
export async function removeRule(options?: { [key: string]: any }) {
  return request<Record<string, any>>('/api/rule', {
    method: 'POST',
    data: {
      method: 'delete',
      ...(options || {}),
    },
  });
}


